import { useRouterState, useNavigate } from "@tanstack/react-router";
import { MainSidebar } from "./MainSidebar";
import { PageContainer } from "./PageContainer";
import { PageHeaderProvider } from "./PageHeaderContext";
import { SubNavBar } from "./SubNavBar";
import { TopBar } from "./TopBar";
import { LoadingPage } from "./LoadingPage";
import { ErrorPage } from "./ErrorPage";
import { navigationSections } from "@/shared/config/navigation";
import { useCurrentAccount } from "@/entities/account/model/queries";
import { usePermissions } from "@/entities/session/hooks/usePermissions";
import { hasAnyPermission } from "@/shared/config/permissionLogic";
import type { MyProfileResponse } from "@/entities/employee/model/types";
import { useNotificationStream } from "@/entities/notification";
import { useIncidentRealtime } from "@/entities/incident";
import { CriticalAckBanner } from "@/features/notification-center";
import { SubscriptionBanner } from "@/features/billing/ui/SubscriptionBanner";
import { useChatStream } from "@/entities/chat";
import { ChatDock } from "@/features/chat";

type OrgShellProps = {
  children: React.ReactNode;
  currentUser: MyProfileResponse | undefined;
};

const findActiveSection = (currentPath: string) => {
  return navigationSections.find(section => {
    if (!section.path) return false;
    if (section.path === currentPath) return true;
    return currentPath.startsWith(`${section.path}/`);
  });
};

/**
 * Which rail icon to light up. A sub-item may sit outside its section's path prefix, so it is
 * matched separately — and only for the highlight, because gating the page on the section's
 * permission would lock out anyone who holds the sub-item's permission but not the section's.
 */
const findHighlightSection = (currentPath: string) => {
  return (
    findActiveSection(currentPath) ??
    navigationSections.find(section =>
      section.subItems?.some(
        item => currentPath === item.path || currentPath.startsWith(`${item.path}/`),
      ),
    )
  );
};

export function OrgShell({ children, currentUser }: OrgShellProps) {
  useNotificationStream();
  useIncidentRealtime();
  useChatStream();

  const {
    data: account,
    isLoading: isAccountLoading,
    error: accountError,
  } = useCurrentAccount();

  const {
    isLoading: isPermissionsLoading,
    hasPermission,
  } = usePermissions();

  const router = useRouterState();
  const navigate = useNavigate();

  const currentPageSection = findActiveSection(router.location.pathname);

  const isLoading = isAccountLoading || isPermissionsLoading;
  if (isLoading) {
    return <LoadingPage />;
  }

  if (accountError) {
    return (
      <ErrorPage
        title="Failed to Load Application"
        message="Unable to load your account information. Please try logging in again."
      />
    );
  }

  const isAuthorized = hasAnyPermission(hasPermission, currentPageSection?.permission);

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-ink">403</h1>
          <h2 className="text-xl font-semibold text-ink-2">Access Denied</h2>
          <p className="text-ink-3">
            {currentPageSection?.permissionDeniedMessage ?? "You do not have permission to view this page."}
          </p>
          <button
            onClick={() => navigate({ to: "/" })}
            className="px-4 py-2 bg-brand-accent text-white rounded hover:bg-brand-accent-hi transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <PageHeaderProvider>
      <div className="flex min-h-screen bg-bg">
        <MainSidebar
          sections={navigationSections}
          activeSection={findHighlightSection(router.location.pathname)}
          currentUser={currentUser}
          logoUrl={account?.logoUrl ?? null}
        />
        <div className="ml-20 flex min-h-screen flex-1 flex-col">
          <TopBar />
          <SubNavBar />
          <SubscriptionBanner />
          <CriticalAckBanner />
          <div className="flex min-h-0 flex-1">
            <main className="min-w-0 flex-1 overflow-auto">
              <PageContainer>{children}</PageContainer>
            </main>
          </div>
          <ChatDock />
        </div>
      </div>
    </PageHeaderProvider>
  );
}
